import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  withRepeat,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  useDerivedValue,
  cancelAnimation,
} from 'react-native-reanimated';
import { withPause } from 'react-native-redash';

import {
  INTERACTIVE_CONSTANTS,
  InteractiveButtons,
} from '@/components/interactive';

type AnimatedSquareProps = {
  withoutRepeat?: boolean;
  withoutPause?: boolean;
};

const SIZE = 100;

export const AnimatedSquare: React.FC<AnimatedSquareProps> = ({
  withoutRepeat = false,
  withoutPause = false,
}: AnimatedSquareProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const isPaused = useDerivedValue(() => {
    return !isPlaying;
  }, [isPlaying]);
  const progress = useSharedValue(0);

  const rStyle = useAnimatedStyle(() => {
    const rotate = `${progress.value * 360}deg`;
    const borderRadius = progress.value * 20;
    const scale = interpolate(progress.value, [0, 1], [0.5, 1]);
    const opacity = interpolate(progress.value, [0, 1], [0.5, 1]);

    return {
      borderRadius,
      transform: [
        {
          scale,
        },
        {
          rotate,
        },
      ],
      opacity,
    };
  });

  useEffect(() => {
    progress.value = withPause(
      withRepeat(
        withTiming(1, { duration: 2000 }),
        withoutRepeat ? 1 : -1,
        true
      ),
      isPaused
    );
  }, [isPaused, progress, withoutRepeat]);

  const buttons = useMemo(() => {
    if (withoutRepeat) {
      return [
        {
          type: 'play',
          onClick: () => {
            cancelAnimation(progress);
            progress.value = 0;
            progress.value = withTiming(1, { duration: 2000 });
          },
        },
      ] as const;
    }

    if (withoutPause) {
      return [
        {
          type: 'play',
          onClick: () => {
            cancelAnimation(progress);
            progress.value = 0;
            progress.value = withRepeat(
              withTiming(1, { duration: 2000 }),
              -1,
              true
            );
          },
        },
        {
          type: 'reset',
          onClick: () => {
            cancelAnimation(progress);
            progress.value = 0;
          },
        },
      ] as const;
    }

    return [
      {
        type: 'play-pause',
        isPlaying,
        onClick: () => setIsPlaying((prev) => !prev),
      },
    ] as const;
  }, [withoutRepeat, withoutPause, isPlaying, progress]);

  return (
    <View style={styles.container}>
      <Animated.View
        style={[
          {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            height: SIZE,
            width: SIZE,
          },
          rStyle,
        ]}
      />
      <InteractiveButtons buttons={buttons as any} theme="light" />
    </View>
  );
};

export const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: INTERACTIVE_CONSTANTS.THEME.DARK.BACKGROUND,
  },
});
